import React, { useState } from 'react';
import { Box, Container, Typography, Paper, Grid, TextField, Button, Alert, CircularProgress } from '@mui/material';
import { Email, Article, Upcoming } from '@mui/icons-material';
import Navigation from './Navigation';
import { contactContent } from '../content/pages/contact';

// Icon mapping
const IconMap: Record<string, React.ReactElement> = {
  Email: <Email sx={{ mr: 2, color: 'primary.main' }} />,
  Article: <Article sx={{ mr: 2, color: 'primary.main' }} />,
  Upcoming: <Upcoming sx={{ mr: 2, color: 'primary.main' }} />,
};

const Contact: React.FC = () => {
  const c = contactContent;
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const config = window.__APP_CONFIG__;
      const baseUrl = config?.API_BASE_URL || 'http://localhost:8080/api';
      
      const response = await fetch(`${baseUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          brand: 'familycast'
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          message: ''
        });
      } else {
        const errorData = await response.json();
        setErrorMessage(errorData.error?.message || c.form.defaultErrorMessage);
        setSubmitStatus('error');
      }
    } catch {
      setErrorMessage(c.form.networkErrorMessage);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Box sx={{
        color: 'white',
        py: 8,
        bgcolor: 'primary.main',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Beaver Background Image */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(/images/beaver-happy-family.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.15,
          zIndex: 0,
        }} />

        {/* Navigation */}
        <Navigation />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ mt: 4 }}>
            <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
              {c.hero.title}
            </Typography>
            <Typography variant="h5" component="h2" sx={{ opacity: 0.9 }}>
              {c.hero.subtitle}
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" textAlign="center" gutterBottom>
          {c.section.title}
        </Typography>
        <Typography variant="h6" textAlign="center" sx={{ color: "#5A5856", mb: 6 }}>
          {c.section.subtitle}
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 4 }}>
              <Typography variant="h4" gutterBottom>
                {c.form.title}
              </Typography>

              {submitStatus === 'success' && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  {c.form.successMessage}
                </Alert>
              )}

              {submitStatus === 'error' && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {errorMessage}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={c.form.fields.firstName}
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      variant="outlined"
                      required
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={c.form.fields.lastName}
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      variant="outlined"
                      required
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={c.form.fields.email}
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      variant="outlined"
                      required
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={c.form.fields.message}
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      variant="outlined"
                      multiline
                      rows={4}
                      required
                      disabled={isSubmitting}
                      inputProps={{ maxLength: 1000 }}
                      helperText={`${formData.message.length}${c.form.fields.messageHelper}`}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      fullWidth
                      disabled={isSubmitting}
                      startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                      sx={{
                        bgcolor: 'primary.main',
                        '&:hover': { bgcolor: 'primary.dark' }
                      }}
                    >
                      {isSubmitting ? c.form.button.submitting : c.form.button.default}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 4 }}>
              <Typography variant="h4" gutterBottom>
                {c.contactInfo.title}
              </Typography>
              <Box sx={{ mt: 3 }}>
                {c.contactInfo.items.map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    {IconMap[item.iconName]}
                    <Box>
                      <Typography variant="h6">{item.title}</Typography>
                      {item.link ? (
                        <Typography
                          variant="body1"
                          component="a"
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: "#5A5856",
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                        >
                          {item.value}
                        </Typography>
                      ) : (
                        <Typography variant="body1" sx={{ color: "#5A5856" }}>
                          {item.value}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ color: "#5A5856", fontSize: '0.9rem', fontStyle: 'italic', mt: 0.5 }}>
                        {item.subtext}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Contact;
